import { action, atom } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import { Howl } from 'howler'
import i18next from 'i18next'

import { ApplinkURL, Deeplink } from '@root/entities/deeplinking'
import { pairRegistry } from '@root/entities/pair'
import { PairAddress } from '@root/entities/pair/model'
import { trackingWalletAlertEvent } from '@root/features/realtime/reatom-websocket-main'
import { environmentAtom, STORAGE_PREFIX, StringUtil } from '@root/shared'
import ALERT_SOUND from '@root/shared/assets/notif.wav'
import { formatMarketCapShort, NumericFormat } from '@root/shared/core/format'
import {
  showNotification,
  type ShowNotificationOptions,
} from '@root/shared/lib/tauri'

import { TrackedWalletSwapAlert } from '../model'

import { trackingWalletsByAddressAtom } from './reatom-tracking-wallets'

import type { Trade } from '@root/entities/trade'

const alertHowl = new Howl({
  src: [ALERT_SOUND],
})

export const soundNotificationsEnabledAtom = atom(
  false,
  'soundNotificationsEnabledAtom',
).pipe(
  withLocalStorage({ key: `${STORAGE_PREFIX}/v1/alert-sound-notifications` }),
)
export const toggleSoundNotificationsActions = action((ctx) => {
  soundNotificationsEnabledAtom(ctx, (enabled) => !enabled)
}, 'toggleSoundNotificationsActions')

export const pushNotificationsEnabledAtom = atom(
  false,
  'pushNotificationsEnabledAtom',
).pipe(
  withLocalStorage({ key: `${STORAGE_PREFIX}/v1/alert-push-notifications` }),
)
export const pushSoundNotificationsActions = action((ctx) => {
  pushNotificationsEnabledAtom(ctx, (enabled) => !enabled)
}, 'pushSoundNotificationsActions')

export const initializeAlertsNotificationsAction = action((_ctx) => {
  trackingWalletAlertEvent.onCall((ctx, data) => {
    const soundsEnabled = ctx.get(soundNotificationsEnabledAtom)

    if (soundsEnabled) {
      alertHowl.play()
    }

    const pushEnabled = ctx.get(pushNotificationsEnabledAtom)
    if (pushEnabled) {
      const {
        assets: { tokenLogosHost, avatarsHost },
      } = ctx.get(environmentAtom)
      const pair = data.pairInfo
        ? pairRegistry.resolveFromDto(ctx, data.pairInfo)
        : undefined
      const trade = TrackedWalletSwapAlert.fromDto(
        data,
        tokenLogosHost,
        avatarsHost,
        pair,
      )
      const opts = notificationFromTradeAction(ctx, trade)

      const deeplink = new Deeplink.Trade({
        network: trade.network,
        pair: PairAddress(trade.pairAddress),
        maker: trade.maker.address,
      })

      const overallChecks = data.pairInfo?.audit.overAllChecks
      const auditLine = overallChecks
        ? `\nAudit checks: ${overallChecks.safeCount}/${
            overallChecks.safeCount +
            overallChecks.warningCount +
            overallChecks.dangerCount
          }`
        : ''
      const logoUrl = trade.quoteCurrency
        ? ctx.get(trade.quoteCurrency.icon)
        : undefined

      const actionUrl = ApplinkURL.getURLFor('Trade', deeplink).toString()
      showNotification({
        ...opts,
        description: opts.description + auditLine,
        imageUrl: logoUrl?.startsWith('http') ? logoUrl : undefined,
        buttonLabel: i18next.t('common.open'),
        actionUrl,
        sound: soundsEnabled ? 'default' : undefined,
      })
    }
  })
}, 'initializeAlertsNotificationsAction')

const notificationFromTradeAction = action(
  (ctx, trade: Trade.Trade): ShowNotificationOptions => {
    const labelsByAddress = ctx.get(trackingWalletsByAddressAtom)
    const makerLabel =
      trade.maker.label ??
      labelsByAddress[trade.maker.address]?.label ??
      StringUtil.truncateMiddle(trade.maker.address, 5, 4)

    const mcapFormatted = formatMarketCapShort(trade.marketCapUsd)

    const balanceAfterFormatted = NumericFormat.formatPreset(
      trade.maker.balanceCurrency,
      'currency-amount-short',
      { compactDecimalZeroes: false },
    )

    const amountCurrencyFormatted = NumericFormat.formatPreset(
      trade.currencyAmount,
      'currency-amount-short',
      { compactDecimalZeroes: false },
    )

    const tickerFormatted = StringUtil.truncate(
      trade.quoteCurrency?.ticker ?? '',
      8,
    )

    if (trade.type === 'buy') {
      if (trade.maker.balanceCurrencyBefore.eq(0)) {
        return {
          title: i18next.t(
            'wallet-tracker.notification.wallet-first-buy.title',
            {
              label: makerLabel,
              ticker: tickerFormatted,
            },
          ),
          description: i18next.t(
            'wallet-tracker.notification.wallet-first-buy.description',
            {
              label: makerLabel,
              amount: amountCurrencyFormatted,
              ticker: tickerFormatted,
              mcap: mcapFormatted,
            },
          ),
        }
      }

      return {
        title: i18next.t('wallet-tracker.notification.wallet-buy-more.title', {
          label: makerLabel,
          ticker: tickerFormatted,
        }),
        description: i18next.t(
          'wallet-tracker.notification.wallet-buy-more.description',
          {
            label: makerLabel,
            amount: amountCurrencyFormatted,
            mcap: mcapFormatted,
            balance: balanceAfterFormatted,
            ticker: tickerFormatted,
          },
        ),
      }
    }

    if (trade.maker.balanceCurrency.eq(0)) {
      return {
        title: i18next.t('wallet-tracker.notification.wallet-sell-full.title', {
          label: makerLabel,
          ticker: tickerFormatted,
        }),
        description: i18next.t(
          'wallet-tracker.notification.wallet-sell-full.description',
          {
            label: makerLabel,
            amount: amountCurrencyFormatted,
            mcap: mcapFormatted,
            ticker: tickerFormatted,
          },
        ),
      }
    }

    return {
      title: i18next.t(
        'wallet-tracker.notification.wallet-sell-partial.title',
        {
          label: makerLabel,
          ticker: tickerFormatted,
        },
      ),
      description: i18next.t(
        'wallet-tracker.notification.wallet-sell-partial.description',
        {
          label: makerLabel,
          amount: amountCurrencyFormatted,
          mcap: mcapFormatted,
          balance: balanceAfterFormatted,
          ticker: tickerFormatted,
        },
      ),
    }
  },
  'notificationFromTradeAction',
)
